import React, { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid, Bookmark, MessageSquare, Folder, Compass,
  Inbox, FileText, User,
  ClipboardCheck, Map, Briefcase, List, BookOpen,
  Users, Award, BarChart3, Settings,
  LogOut, Heart, PanelLeftClose, PanelLeftOpen,
  CreditCard, Bell,
} from "lucide-react";
import { useRole } from "../context/RoleContext";
import { navForUser } from "../lib/roles";
import { LogoLockup, LogoMark } from "./Logo";
import {
  unreadCount,
  onNotificationsUpdated,
} from "../lib/notifications";

const ICONS = {
  "layout-grid": LayoutGrid,
  bookmark: Bookmark,
  heart: Heart,
  "message-square": MessageSquare,
  folder: Folder,
  compass: Compass,
  inbox: Inbox,
  "file-text": FileText,
  user: User,
  "clipboard-check": ClipboardCheck,
  map: Map,
  briefcase: Briefcase,
  list: List,
  "book-open": BookOpen,
  users: Users,
  award: Award,
  "bar-chart-3": BarChart3,
  settings: Settings,
  "credit-card": CreditCard,
  bell: Bell,
};

const COLLAPSED = 64;
const EXPANDED = 240;
const RAIL_PREF_KEY = "wn.rail.collapsed.v1";

function readInitialCollapsed() {
  try {
    return localStorage.getItem(RAIL_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

function Identity({ user, expanded }) {
  const initials = (user?.name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className="flex items-center"
      style={{ gap: 12, padding: expanded ? "10px 16px" : "10px 0", justifyContent: expanded ? "flex-start" : "center" }}
      data-testid="rail-identity"
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 999,
          background: "#8F3C23",
          color: "#FBFAFD",
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 700,
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {initials}
      </div>
      {expanded && (
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "#FBFAFD",
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
            }}
          >
            {user?.name || "—"}
          </div>
          <div
            className="wn-label"
            style={{ color: "#9AA0C0", letterSpacing: "0.08em", marginTop: 2 }}
          >
            {(user?.role || "").replace(/_/g, " ")}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeftRail() {
  const { role, user, signOut } = useRole();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(readInitialCollapsed);
  const expanded = !collapsed;
  const baseItems = navForUser(role, user);
  const w = expanded ? EXPANDED : COLLAPSED;
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(async () => {
    if (!user?.id) return;
    try {
      setUnread(await unreadCount(user.id));
    } catch {
      /* silent — rail badge is a background poller */
    }
  }, [user?.id]);

  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, 30000);
    return () => clearInterval(id);
  }, [refreshUnread]);

  useEffect(() => {
    return onNotificationsUpdated(refreshUnread);
  }, [refreshUnread]);

  const items = baseItems.map((it) =>
    it.icon === "bell" ? { ...it, badge: unread } : it,
  );

  // Where the logo should route a signed-in user. Map each role to its
  // dashboard so the brand mark acts as a "home in the portal" anchor
  // rather than dropping the user back on the public landing page.
  const ROLE_HOME = {
    owner_builder: "/owner",
    architect: "/architect",
    city_staff: "/city",
    platform_admin: "/admin",
  };
  const brandHref = ROLE_HOME[role] || "/";

  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(RAIL_PREF_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  function onSignOut() {
    signOut();
    navigate("/");
  }

  return (
    <>
      {/* Placeholder occupies the rail's current width so main content reflows. */}
      <div
        style={{
          width: w,
          flexShrink: 0,
          transition: "width 180ms ease-out",
        }}
        aria-hidden
      />
      <aside
        data-testid="left-rail"
        data-collapsed={collapsed ? "true" : "false"}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: w,
          background: "#1B1F4B",
          color: "#FBFAFD",
          zIndex: 50,
          transition: "width 180ms ease-out",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Brand mark + collapse toggle */}
        <div
          style={{
            display: "flex",
            flexDirection: expanded ? "row" : "column",
            alignItems: "center",
            justifyContent: expanded ? "space-between" : "center",
            gap: expanded ? 8 : 10,
            padding: expanded ? "16px 12px 16px 18px" : "16px 0 12px",
            borderBottom: "1px solid rgba(91,97,150,0.20)",
            flexShrink: 0,
          }}
        >
          <Link
            to={brandHref}
            data-testid="rail-brand"
            title="Go to your dashboard"
            style={{
              display: "inline-flex",
              alignItems: "center",
              color: "#FBFAFD",
              textDecoration: "none",
              minWidth: 0,
            }}
          >
            {expanded ? <LogoLockup tone="white" size={24} /> : <LogoMark tone="white" size={26} />}
          </Link>
          <button
            type="button"
            onClick={toggleCollapsed}
            data-testid="rail-collapse-toggle"
            aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
            title={expanded ? "Collapse navigation" : "Expand navigation"}
            style={{
              width: 30,
              height: 30,
              padding: 0,
              borderRadius: 6,
              background: "transparent",
              border: "1px solid rgba(91,97,150,0.35)",
              color: "#9AA0C0",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition:
                "background-color 140ms ease, color 140ms ease, border-color 140ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#2A2E5E";
              e.currentTarget.style.color = "#FBFAFD";
              e.currentTarget.style.borderColor = "rgba(91,97,150,0.55)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#9AA0C0";
              e.currentTarget.style.borderColor = "rgba(91,97,150,0.35)";
            }}
          >
            {expanded ? (
              <PanelLeftClose size={16} strokeWidth={1.7} />
            ) : (
              <PanelLeftOpen size={16} strokeWidth={1.7} />
            )}
          </button>
        </div>

        {/* Floating toggle when collapsed — appears just under the brand mark
            so users can re-expand without hunting. */}
        {!expanded && null}

        {/* Nav items */}
        <nav
          data-testid="rail-nav"
          style={{ flex: 1, paddingTop: 14, paddingBottom: 14, overflowY: "auto" }}
        >
          {items.map((it, i) =>
            it.divider ? (
              <hr
                key={`d-${i}`}
                style={{
                  margin: "14px 14px",
                  border: "none",
                  borderTop: "1px solid rgba(91,97,150,0.20)",
                }}
                aria-hidden
              />
            ) : (
              <RailItem key={it.to} item={it} expanded={expanded} />
            ),
          )}
        </nav>

        {/* Bottom actions: identity + sign out */}
        <div
          style={{
            borderTop: "1px solid rgba(91,97,150,0.20)",
            paddingTop: 8,
            paddingBottom: 12,
            flexShrink: 0,
          }}
        >
          <Identity user={user} expanded={expanded} />

          <button
            type="button"
            onClick={onSignOut}
            data-testid="rail-sign-out"
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: expanded ? "10px 16px" : "10px 0",
              justifyContent: expanded ? "flex-start" : "center",
              gap: 14,
              background: "transparent",
              border: "none",
              color: "#9AA0C0",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 500,
              transition: "background-color 140ms ease, color 140ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#2A2E5E";
              e.currentTarget.style.color = "#FBFAFD";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#9AA0C0";
            }}
          >
            <LogOut size={20} strokeWidth={1.6} />
            {expanded && <span>Sign out</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

function RailItem({ item, expanded }) {
  const Icon = ICONS[item.icon] || LayoutGrid;
  return (
    <NavLink
      to={item.to}
      end={item.end === undefined ? false : item.end}
      data-testid={`rail-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      style={({ isActive }) => ({
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: 44,
        padding: expanded ? "0 16px 0 18px" : 0,
        justifyContent: expanded ? "flex-start" : "center",
        gap: 14,
        color: isActive ? "#FBFAFD" : "#9AA0C0",
        backgroundColor: isActive ? "rgba(177,78,47,0.18)" : "transparent",
        textDecoration: "none",
        fontSize: 13.5,
        fontWeight: isActive ? 700 : 500,
        transition: "background-color 140ms ease, color 140ms ease",
      })}
      onMouseEnter={(e) => {
        if (!e.currentTarget.style.backgroundColor.startsWith("rgba(177")) {
          e.currentTarget.dataset.hovered = "y";
          e.currentTarget.style.backgroundColor = "#2A2E5E";
          e.currentTarget.style.color = "#FBFAFD";
        }
      }}
      onMouseLeave={(e) => {
        if (e.currentTarget.dataset.hovered === "y") {
          e.currentTarget.dataset.hovered = "";
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "#9AA0C0";
        }
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: "#B14E2F",
                borderRadius: 0,
              }}
            />
          )}
          <Icon
            size={20}
            strokeWidth={1.6}
            style={{ flexShrink: 0, color: isActive ? "#FBFAFD" : undefined }}
          />
          {expanded && (
            <span style={{ whiteSpace: "nowrap", flex: 1 }}>{item.label}</span>
          )}
          {item.badge != null && item.badge > 0 && (
            <span
              data-testid={`rail-badge-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="tabular-nums"
              style={{
                ...(expanded
                  ? { marginRight: 0 }
                  : {
                      position: "absolute",
                      top: 6,
                      right: 8,
                    }),
                minWidth: 18,
                height: 18,
                padding: "0 5px",
                borderRadius: 999,
                background: "#B14E2F",
                color: "#FBFAFD",
                fontSize: 10,
                fontWeight: 800,
                lineHeight: "18px",
                textAlign: "center",
                border: expanded ? "none" : "1.5px solid #1B1F4B",
              }}
            >
              {item.badge > 99 ? "99+" : item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
